import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore, useHasHydrated, useIsAuthenticated } from '@/stores/authStore';
import { AuthPageLayout } from '@components/auth/AuthPageLayout';
import { GoogleAuthSection } from '@components/auth/GoogleSignInButton';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { PasswordStrengthIndicator } from '@components/auth/PasswordStrengthIndicator';
import { validatePassword } from '@/utils/passwordSecurity';

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useAuthStore((state) => state.register);
  const isLoading = useAuthStore((state) => state.isLoading);
  const authError = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const isAuthenticated = useIsAuthenticated();
  const hasHydrated = useHasHydrated();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    clearError();
  }, [clearError]);

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
    }

    const passwordValidation = validatePassword(password);
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.error || 'Password does not meet security requirements';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await register(email, password, name.trim());
      toast.success('Account created — welcome to Khiimori!');
      navigate('/home');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      if (message.toLowerCase().includes('already exists')) {
        toast.error('An account with this email already exists. Try signing in instead.');
      } else {
        toast.error(message);
      }
    }
  };

  if (hasHydrated && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return (
    <AuthPageLayout
      title="Create your account"
      subtitle="Get a workspace for smarter tab management, AI suggestions, and one-click app generation."
      alternatePrompt="Already have an account?"
      alternateLinkTo="/login"
      alternateLinkLabel="Sign in"
      legalAction="creating an account"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {authError && (
          <p
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
            role="alert"
          >
            {authError.includes('already exists')
              ? 'This email is already registered. Sign in below or use a different email.'
              : authError}
          </p>
        )}

        <Input
          label="Full name"
          type="text"
          placeholder="Jane Smith"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (authError) clearError();
          }}
          error={errors.name}
          autoComplete="name"
          aria-required="true"
        />

        <Input
          label="Work email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (authError) clearError();
          }}
          error={errors.email}
          autoComplete="email"
          aria-required="true"
        />

        <div>
          <Input
            label="Password"
            type="password"
            placeholder="Min 8 chars with upper, lower, number, special"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (authError) clearError();
            }}
            error={errors.password}
            autoComplete="new-password"
            aria-required="true"
            passwordToggle
          />
          <PasswordStrengthIndicator password={password} />
        </div>

        <Input
          label="Confirm password"
          type="password"
          placeholder="Re-enter your password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (authError) clearError();
          }}
          error={errors.confirmPassword}
          autoComplete="new-password"
          aria-required="true"
          passwordToggle
        />

        <Button
          type="submit"
          className="w-full"
          isLoading={isLoading}
          aria-label="Create your account"
        >
          Create account
        </Button>

        <GoogleAuthSection label="Sign up with Google" />
      </form>
    </AuthPageLayout>
  );
}
